/**
 * Portfolio Selectors
 * 
 * ⚠️ CRITICAL: Per ADR-FE-001, selectors ONLY access pre-calculated data from backend.
 * NO calculations, aggregations, or business logic in selectors.
 * All data is pre-calculated by Portfolio Service backend.
 */

import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../index';

/**
 * Select consolidated portfolio
 * ✅ GOOD: Just access pre-calculated data
 */
export const selectConsolidatedPortfolio = createSelector(
  [(state: RootState) => state.portfolio.consolidated],
  (consolidated) => consolidated
);

/**
 * Select portfolio summary
 * ✅ GOOD: Access pre-calculated summary from backend
 */
export const selectPortfolioSummary = createSelector(
  [(state: RootState) => state.portfolio.consolidated],
  (consolidated) => consolidated?.portfolio_summary || null
);

/**
 * Select total portfolio value
 * ✅ GOOD: Access pre-calculated value
 */
export const selectTotalPortfolioValue = createSelector(
  [(state: RootState) => state.portfolio.consolidated],
  (consolidated) => consolidated?.portfolio_summary?.total_market_value || 0
);

/**
 * Select total P&L
 * ✅ GOOD: Access pre-calculated P&L
 */
export const selectTotalPnL = createSelector(
  [(state: RootState) => state.portfolio.consolidated],
  (consolidated) => consolidated?.portfolio_summary?.total_unrealized_pnl || 0
);

/**
 * Select total P&L percentage
 * ✅ GOOD: Access pre-calculated percentage
 */
export const selectTotalPnLPercentage = createSelector(
  [(state: RootState) => state.portfolio.consolidated],
  (consolidated) => consolidated?.portfolio_summary?.total_unrealized_pnl_percentage || 0
);

/**
 * Select day change
 * ✅ GOOD: Access pre-calculated day change
 */
export const selectDayChange = createSelector(
  [(state: RootState) => state.portfolio.consolidated],
  (consolidated) => consolidated?.portfolio_summary?.total_day_change || 0
);

/**
 * Select day change percentage
 * ✅ GOOD: Access pre-calculated day change percentage
 */
export const selectDayChangePercentage = createSelector(
  [(state: RootState) => state.portfolio.consolidated],
  (consolidated) => consolidated?.portfolio_summary?.total_day_change_percentage || 0
);

/**
 * Select top holdings
 * ✅ GOOD: Access pre-aggregated holdings from backend
 */
export const selectTopHoldings = createSelector(
  [(state: RootState) => state.portfolio.consolidated],
  (consolidated) => consolidated?.top_holdings || []
);

/**
 * Select all holdings (detailed)
 * ✅ GOOD: Access holdings list from backend
 */
export const selectAllHoldings = createSelector(
  [(state: RootState) => state.portfolio.holdings],
  (holdings) => holdings
);

/**
 * Select holdings by broker account
 * ✅ GOOD: Access grouped holdings (grouping done in reducer, not calculation)
 */
export const selectHoldingsByBroker = createSelector(
  [(state: RootState) => state.portfolio.byBroker],
  (byBroker) => byBroker
);

/**
 * Select broker account summaries
 * ✅ GOOD: Access pre-calculated broker summaries
 */
export const selectBrokerAccountSummaries = createSelector(
  [(state: RootState) => state.portfolio.consolidated],
  (consolidated) => consolidated?.broker_accounts || []
);

/**
 * Select portfolio loading state
 */
export const selectPortfolioLoading = createSelector(
  [(state: RootState) => state.portfolio.loading],
  (loading) => loading
);

/**
 * Select portfolio error
 */
export const selectPortfolioError = createSelector(
  [(state: RootState) => state.portfolio.error],
  (error) => error
);

/**
 * Select last updated timestamp
 */
export const selectPortfolioLastUpdated = createSelector(
  [(state: RootState) => state.portfolio.lastUpdated],
  (lastUpdated) => lastUpdated
);

/**
 * Select sync in progress status
 */
export const selectSyncInProgress = createSelector(
  [(state: RootState) => state.portfolio.syncInProgress],
  (syncInProgress) => syncInProgress
);

/**
 * Select sync status for a specific broker account
 */
export const selectBrokerSyncStatus = (brokerAccountId: string) =>
  createSelector(
    [(state: RootState) => state.portfolio.syncInProgress],
    (syncInProgress) => syncInProgress[brokerAccountId] || false
  );

